import { FC, ReactNode, useEffect, useState } from 'react';
import { captureException } from '@sentry/react';
import { TbLink } from 'react-icons/tb';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from '@/shared/components/toasts/PlatformToast';

import { useKey } from '@/hooks/useKey';
import { usePlatformHotkeys as useHotkeys } from '@/hooks/usePlatformHotkeys';
import RenameResourceModal from '@/shared/components/modals/ResourceActionModals/01-RenameResourceModal';
import DeleteResourceModal from '@/shared/components/modals/ResourceActionModals/03-DeleteResourceModal';
import { useTeamStore } from '@/stores';
import { Team } from '@/types';

interface TeamKeyboardShortcutsWrapperProps {
    team: Team;
    children?: ReactNode;
}

const TeamKeyboardShortcutsWrapper: FC<TeamKeyboardShortcutsWrapperProps> = ({ children, team }): JSX.Element => {
    const navigate = useNavigate();
    const { teamId } = useParams<{ teamId: string }>();

    const url = `/settings/workspace/permissions/teams/${teamId ?? team.id}`;

    const location = useLocation();

    // Copy to Clipboard
    useHotkeys('alt+c', () => {
        navigator.clipboard
            .writeText(
                `${
                    import.meta.env.DEV ? 'http://localhost:3000' : 'https://dashboard.countercyclical.io'
                }/settings/workspace/permissions/teams/${team?.id}`,
            )
            .then(
                () => {
                    toast('Link Copied', {
                        icon: <TbLink size='1.125rem' style={{ marginRight: '0.5rem' }} />,
                        description: team?.name,
                    });
                },
                (error) => {
                    toast('Could not copy link to clipboard');

                    captureException(error);
                },
            );
    });

    const { toggleTeamFavorite, toggleTeamLock, toggleTeamArchive } = useTeamStore();

    useHotkeys('alt+f', () => {
        toggleTeamFavorite(team?.id, !team?.favorited);
    });

    useHotkeys('shift+alt+l', () => {
        toggleTeamLock(team?.id, !team?.locked);
    });

    useHotkeys('shift+alt+a', () => {
        toggleTeamArchive(team?.id, !team?.archived);
    });

    const [renameModalVisible, setRenameModalVisible] = useState<boolean>(false);
    const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);

    useHotkeys('alt+r', () => {
        setRenameModalVisible((b: boolean) => !b);
    });

    useHotkeys('alt+backspace', () => {
        setDeleteModalVisible((b: boolean) => !b);
    });

    // Keyboard Shortcuts - Move Tabs Left/Right
    useHotkeys('shift+alt+1', () => {
        navigate(`${url}/members`);
    });
    useHotkeys('shift+alt+2', () => {
        navigate(`${url}/settings`);
    });

    const paths = ['members', 'settings'];

    const currentTab = location.pathname.replace(`${url}/`, '') as string;

    const [previousRoute, setPreviousRoute] = useState<any>();
    const [nextRoute, setNextRoute] = useState<any>();

    useEffect(() => {
        const currentIndex = paths.indexOf(currentTab);

        if (currentIndex === 0) {
            setPreviousRoute(paths.length - 1);
        } else {
            setPreviousRoute(currentIndex - 1);
        }

        if (currentIndex === paths.length - 1) {
            setNextRoute(0);
        } else {
            setNextRoute(currentIndex + 1);
        }
    }, [location, currentTab]);

    const handleArrowLeft = () => {
        navigate(`${url}/${paths[previousRoute]}`);
    };

    const handleArrowRight = () => {
        navigate(`${url}/${paths[nextRoute]}`);
    };

    useKey('ArrowLeft', handleArrowLeft);
    useKey('ArrowRight', handleArrowRight);

    return (
        <>
            {children}
            <RenameResourceModal
                resource={team}
                open={renameModalVisible}
                setOpen={setRenameModalVisible}
                singularResource={'team'}
            />
            <DeleteResourceModal
                resource={team}
                open={deleteModalVisible}
                setOpen={setDeleteModalVisible}
                singularResource={'team'}
            />
        </>
    );
};

export default TeamKeyboardShortcutsWrapper;
